/**
 * Enhanced Theme Toggle Component
 *
 * Features:
 * - Visual toggle between light/dark/system modes
 * - Keyboard shortcut (Ctrl+Shift+D)
 * - Smooth transition animations
 * - Accessible with ARIA labels
 */

import { useTheme, type ThemeMode } from '@/contexts/ThemeContext';
import { useEffect, useState } from 'react';
import { SunOutlined, MoonOutlined, DesktopOutlined } from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';

export function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [resolvedTheme, setTheme]);

  const getIcon = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return <SunOutlined className="text-base" />;
      case 'dark':
        return <MoonOutlined className="text-base" />;
      case 'system':
        return <DesktopOutlined className="text-base" />;
    }
  };

  const getLabel = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'light',
      icon: <SunOutlined />,
      label: 'Light',
      onClick: () => setTheme('light'),
    },
    {
      key: 'dark',
      icon: <MoonOutlined />,
      label: 'Dark',
      onClick: () => setTheme('dark'),
    },
    {
      key: 'system',
      icon: <DesktopOutlined />,
      label: 'System',
      onClick: () => setTheme('system'),
    },
  ];

  if (!mounted) {
    return (
      <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse" />
    );
  }

  return (
    <Dropdown
      menu={{ items: menuItems, selectedKeys: [theme] }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Tooltip title={`Theme: ${getLabel(theme)} (Ctrl+Shift+D)`} placement="bottom">
        <button
          className="inline-flex items-center justify-center rounded-md p-2 transition-all duration-200 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`Current theme: ${getLabel(theme)}. Click to change theme.`}
        >
          <span className="transition-transform duration-300 hover:scale-110">
            {getIcon(theme)}
          </span>
        </button>
      </Tooltip>
    </Dropdown>
  );
}

/**
 * Simple Theme Toggle Button (without dropdown)
 * Toggles between light and dark mode only
 */
export function SimpleThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse" />
    );
  }

  return (
    <Tooltip title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`} placement="bottom">
      <button
        onClick={toggleTheme}
        className="inline-flex items-center justify-center rounded-md p-2 transition-all duration-200 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        <span className="transition-all duration-300 hover:scale-110">
          {resolvedTheme === 'dark' ? (
            <SunOutlined className="text-base animate-[spin_4s_linear_infinite]" />
          ) : (
            <MoonOutlined className="text-base" />
          )}
        </span>
      </button>
    </Tooltip>
  );
}
